import * as THREE from 'three'
import { Reflector } from 'three/addons/objects/Reflector.js'

// FullScreenQuad helper for post-processing
class FullScreenQuad {
  constructor(material) {
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)
    this.geometry = new THREE.PlaneGeometry(2, 2)
    this.mesh = new THREE.Mesh(this.geometry, material)
  }
  
  render(renderer) {
    renderer.render(this.mesh, this.camera)
  }
  
  get material() {
    return this.mesh.material
  }
  
  set material(value) {
    this.mesh.material = value
  }
}

// RippleEffect from old project (exact copy)
const RippleEffect = (function () {
  function RippleEffect(renderer, width, height) {
    this.renderer = renderer
    this.width = 512
    this.height = 512
    this.delta = new THREE.Vector2(1 / this.width, 1 / this.height)

    this.hMap = new THREE.WebGLRenderTarget(this.width, this.height, { type: THREE.FloatType, depthBuffer: false, stencilBuffer: false })
    this.hMap1 = new THREE.WebGLRenderTarget(this.width, this.height, { type: THREE.FloatType, depthBuffer: false, stencilBuffer: false })
    this.fsQuad = new FullScreenQuad()

    this.initShaders()
  }

  RippleEffect.prototype.initShaders = function () {
    const defaultVertexShader = `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `

    this.copyMat = new THREE.ShaderMaterial({
      uniforms: { 'tDiffuse': { value: null } },
      vertexShader: defaultVertexShader,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        varying vec2 vUv;
        void main() {
          gl_FragColor = texture2D(tDiffuse, vUv);
        }
      `
    })

    this.updateMat = new THREE.ShaderMaterial({
      uniforms: {
        'tDiffuse': { value: null },
        'delta': new THREE.Uniform(this.delta)
      },
      vertexShader: defaultVertexShader,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform vec2 delta;
        varying vec2 vUv;
        void main() {
          vec4 texel = texture2D(tDiffuse, vUv);
          vec2 dx = vec2(delta.x, 0.0);
          vec2 dy = vec2(0.0, delta.y);
          float average = (
            texture2D(tDiffuse, vUv - dx).r +
            texture2D(tDiffuse, vUv - dy).r +
            texture2D(tDiffuse, vUv + dx).r +
            texture2D(tDiffuse, vUv + dy).r
          ) * 0.25;
          texel.g += (average - texel.r) * 2.0;
          texel.g *= 0.995;
          texel.r += texel.g;
          gl_FragColor = texel;
        }
      `
    })

    this.normalsMat = new THREE.ShaderMaterial({
      uniforms: {
        'tDiffuse': { value: null },
        'delta': new THREE.Uniform(this.delta)
      },
      vertexShader: defaultVertexShader,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform vec2 delta;
        varying vec2 vUv;
        void main() {
          vec4 texel = texture2D(tDiffuse, vUv);
          vec3 dx = vec3(delta.x, texture2D(tDiffuse, vec2(vUv.x + delta.x, vUv.y)).r - texel.r, 0.0);
          vec3 dy = vec3(0.0, texture2D(tDiffuse, vec2(vUv.x, vUv.y + delta.y)).r - texel.r, delta.y);
          texel.ba = normalize(cross(dy, dx)).xz;
          gl_FragColor = texel;
        }
      `
    })

    this.dropMat = new THREE.ShaderMaterial({
      uniforms: {
        'tDiffuse': { value: null },
        'center': new THREE.Uniform(new THREE.Vector2()),
        'radius': { value: 0.05 },
        'strength': { value: 0.5 }
      },
      vertexShader: defaultVertexShader,
      fragmentShader: `
        const float PI = 3.1415926535897932384626433832795;
        uniform sampler2D tDiffuse;
        uniform vec2 center;
        uniform float radius;
        uniform float strength;
        varying vec2 vUv;
        void main() {
          vec4 texel = texture2D(tDiffuse, vUv);
          float drop = max(0.0, 1.0 - length(center * 0.5 + 0.5 - vUv) / radius);
          drop = 0.5 - cos(drop * PI) * 0.5;
          texel.r += drop * strength;
          gl_FragColor = texel;
        }
      `
    })
  }

  RippleEffect.prototype.update = function () {
    this.updateHMap()
    this.updateHMapNormals()
  }

  RippleEffect.prototype.updateHMap = function () {
    this.updateMat.uniforms.tDiffuse.value = this.hMap.texture
    this.renderShaderMat(this.updateMat, this.hMap1)
    this.swapBuffers()
  }

  RippleEffect.prototype.updateHMapNormals = function () {
    this.normalsMat.uniforms.tDiffuse.value = this.hMap.texture
    this.renderShaderMat(this.normalsMat, this.hMap1)
    this.swapBuffers()
  }

  RippleEffect.prototype.addDrop = function (x, y, radius, strength) {
    this.dropMat.uniforms.tDiffuse.value = this.hMap.texture
    this.dropMat.uniforms.center.value.set(x, y)
    this.dropMat.uniforms.radius.value = radius
    this.dropMat.uniforms.strength.value = strength
    this.renderShaderMat(this.dropMat, this.hMap1)
    this.swapBuffers()
  }

  RippleEffect.prototype.renderShaderMat = function (mat, target) {
    this.fsQuad.material = mat
    const oldTarget = this.renderer.getRenderTarget()
    this.renderer.setRenderTarget(target)
    this.fsQuad.render(this.renderer)
    this.renderer.setRenderTarget(oldTarget)
  }

  RippleEffect.prototype.swapBuffers = function () {
    const temp = this.hMap
    this.hMap = this.hMap1
    this.hMap1 = temp
  }

  return RippleEffect
})()

export class LiquidGridPlane {
  constructor(parent, options = {}) {
    this.parent = parent
    this.renderer = options.renderer
    this.camera = options.camera
    
    // Set fixed grid dimensions (50x50 square)
    this.gridWWidth = 50
    this.gridWHeight = 50
    
    // Ripple simulation
    this.ripple = null
    this.gridGroup = null
    
    // Mouse interaction
    this.raycaster = new THREE.Raycaster()
    this.mouse = new THREE.Vector2()
    this.mouseOver = false
    
    this.init()
  }
  
  init() {
    this.initRippleEffect()
    this.createGridScene()
  }
  
  initRippleEffect() {
    this.ripple = new RippleEffect(this.renderer, window.innerWidth, window.innerHeight)
  }
  
  createGridScene() {
    this.gridGroup = new THREE.Group()
    this.gridGroup.scale.set(0.4, 0.4, 0.4)  // Scaled up 100% from 0.2 (now 40% of original)
    this.gridGroup.rotation.x = Math.PI / 2
    this.gridGroup.position.y = -2.8
    this.parent.add(this.gridGroup)
    

// Reflector plane removed - using ripple overlay as main reflective surface
    // Ripple overlay mesh with reflector shader
    const rippleOverlayGeometry = new THREE.PlaneGeometry(
      this.gridWWidth,
      this.gridWHeight,
      200,  // More segments for smoother ripples
      100
    )
    
    // Load radial mask texture
    const textureLoader = new THREE.TextureLoader()
    this.radialMask = textureLoader.load('./sect03/radialmask.webp')
    this.radialMask.wrapS = THREE.ClampToEdgeWrapping
    this.radialMask.wrapT = THREE.ClampToEdgeWrapping
    
    // Create second Reflector for the overlay (uses Reflector shader with ripple)
    this.rippleOverlay = new Reflector(rippleOverlayGeometry, {
      color: 0x111122,
      textureWidth: 512,
      textureHeight: 512,
      clipBias: 0.003
    })
    this.rippleOverlay.position.z = 0  // At the same level as original reflector
    this.rippleOverlay.rotation.x = Math.PI  // Flip to face upward
    this.rippleOverlay.getRenderTarget().texture.colorSpace = THREE.SRGBColorSpace
    
    // Enable transparency for the material
    this.rippleOverlay.material.transparent = true
    
    // Add ripple displacement and alpha mask to Reflector's material
    this.rippleOverlay.material.onBeforeCompile = (shader) => {
      shader.uniforms.hmap = { value: this.ripple.hMap.texture }
      shader.uniforms.alphaMask = { value: this.radialMask }
      
      // Add uniforms to vertex shader
      shader.vertexShader = 'uniform sampler2D hmap;\n' + shader.vertexShader
      shader.vertexShader = shader.vertexShader.replace(
        'vUv = textureMatrix * vec4( position, 1.0 );',
        `
        vec4 info = texture2D(hmap, uv);
        vec3 displacedPosition = position;
        displacedPosition.z = 5.0 * info.r;  // Taller ripples
        vUv = textureMatrix * vec4(displacedPosition, 1.0);
        `
      )
      
      // Add alpha mask to fragment shader - must match actual Reflector shader pattern
      shader.fragmentShader = 'uniform sampler2D alphaMask;\nvarying vec2 vAlphaUv;\n' + shader.fragmentShader
      
      // Add varying for alpha UVs in vertex shader
      shader.vertexShader = shader.vertexShader.replace(
        'void main() {',
        `varying vec2 vAlphaUv;
void main() {
  vAlphaUv = uv;`
      )
      
      // Replace the hardcoded alpha with mask - Reflector uses: gl_FragColor = vec4( blendOverlay( base.rgb, color ), 1.0 );
      shader.fragmentShader = shader.fragmentShader.replace(
        'gl_FragColor = vec4( blendOverlay( base.rgb, color ), 1.0 );',
        `
        float mask = texture2D(alphaMask, vAlphaUv).r;
        gl_FragColor = vec4( blendOverlay( base.rgb, color ), mask );
        `
      )
    }
    
    this.gridGroup.add(this.rippleOverlay)
    
    // ADDED: Liquid grid lines from liquid-grid project
    // Add colored point lights at corners
    let pointLight1 = new THREE.PointLight(0xFFFF80)
    pointLight1.position.set(-this.gridWWidth / 2, this.gridWHeight / 2, 50)
    this.gridGroup.add(pointLight1)

    let pointLight2 = new THREE.PointLight(0xde3578)
    pointLight2.position.set(this.gridWWidth / 2, this.gridWHeight / 2, 50)
    this.gridGroup.add(pointLight2)

    let pointLight3 = new THREE.PointLight(0xFF4040)
    pointLight3.position.set(-this.gridWWidth / 2, -this.gridWHeight / 2, 50)
    this.gridGroup.add(pointLight3)

    let pointLight4 = new THREE.PointLight(0x0247e5)
    pointLight4.position.set(this.gridWWidth / 2, -this.gridWHeight / 2, 50)
    this.gridGroup.add(pointLight4)

    // Material with ripple displacement - Mirror-like
    const gridMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xffffff, 
      side: THREE.DoubleSide, 
      metalness: 1.0,      // Full metal = maximum reflection
      roughness: 0.0,      // Perfectly smooth = mirror sharpness
      onBeforeCompile: shader => {
        shader.uniforms.hmap = { value: this.ripple.hMap.texture }
        shader.vertexShader = "uniform sampler2D hmap;\n" + shader.vertexShader
        const token = '#include <begin_vertex>'
        const customTransform = `
          vec3 transformed = vec3(position);
          vec4 info = texture2D(hmap, uv);
          vNormal = vec3(info.b, sqrt(1.0 - dot(info.ba, info.ba)), info.a).xzy;
          transformed.z = 20. * info.r;
        `
        shader.vertexShader = shader.vertexShader.replace(token, customTransform)
      }
    })

    /*
    // COMMENTED OUT: Grid lines (wireframe)
    // Calculate grid divisions - 50x50 squares
    const gridDivisions = 50
    const dx = this.gridWWidth / gridDivisions
    const dy = this.gridWHeight / gridDivisions

    // Create horizontal lines (51 lines for 50 squares)
    for (let j = 0; j <= gridDivisions; j++) {
      const geometry = new THREE.BufferGeometry()
      const positions = []
      const uvs = []
      const y = -this.gridWHeight / 2 + j * dy
      for (let i = 0; i <= gridDivisions; i++) {
        positions.push(-this.gridWWidth / 2 + i * dx, y, 0)
        uvs.push(i / gridDivisions, j / gridDivisions)
      }
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
      geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2))
      geometry.computeBoundingSphere()
      this.gridGroup.add(new THREE.Line(geometry, gridMaterial))
    }

    // Create vertical lines (51 lines for 50 squares)
    for (let i = 0; i <= gridDivisions; i++) {
      const geometry = new THREE.BufferGeometry()
      const positions = []
      const uvs = []
      const x = -this.gridWWidth / 2 + i * dx
      for (let j = 0; j <= gridDivisions; j++) {
        positions.push(x, -this.gridWHeight / 2 + j * dy, 0)
        uvs.push(i / gridDivisions, j / gridDivisions)
      }
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
      geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2))
      geometry.computeBoundingSphere()
      this.gridGroup.add(new THREE.Line(geometry, gridMaterial))
    }
    */

    /*
    // COMMENTED OUT: Solid plane with ripple displacement
    const planeGeometry = new THREE.PlaneGeometry(
      this.gridWWidth,
      this.gridWHeight,
      128,  // High segment count for smooth ripples
      128
    )
    this.ripplePlane = new THREE.Mesh(planeGeometry, gridMaterial)
    this.gridGroup.add(this.ripplePlane)
    */
  }
  
getGridMP(e) {
    this.mouse.x = ((e.clientX / window.innerWidth) * 2 - 1)
    this.mouse.y = (-(e.clientY / window.innerHeight) * 2 + 1)
    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(this.mouse, this.camera)
    
    const intersects = raycaster.intersectObject(this.gridGroup, true)
    
    if (intersects.length === 0) {
      return null
    }
    
    const point = intersects[0].point
    const localPoint = this.gridGroup.worldToLocal(point.clone())
    const gp = { x: 2 * localPoint.x / this.gridWWidth, y: 2 * localPoint.y / this.gridWHeight }
    return gp
  }
  
  updateMouse(clientX, clientY) {
    const e = { clientX, clientY }
    const gp = this.getGridMP(e)
    
    if (!gp) return
    
    this.ripple.addDrop(gp.x, gp.y, 0.1, 0.02)  // Ripple strength halved
  }
  
  setMouseOver(over) {
    this.mouseOver = over
  }
  
update(time, delta = 0.016) {
    if (!this.mouseOver && time) {
      const t = time * 0.001
      const x = Math.cos(t) * 0.2
      const y = Math.sin(t) * 0.2
      this.ripple.addDrop(x, y, 0.05, -0.04)
    }
    
    this.ripple.update()
  }
  
  setVisibility(visible) {
    if (this.gridGroup) {
      this.gridGroup.visible = visible
    }
  }
  
  destroy() {
    if (this.ripple) {
      if (this.ripple.hMap) this.ripple.hMap.dispose()
      if (this.ripple.hMap1) this.ripple.hMap1.dispose()
      this.ripple = null
    }
    
    if (this.rippleOverlay) {
      this.rippleOverlay.dispose()
      this.rippleOverlay = null
    }
    
    if (this.radialMask) {
      this.radialMask.dispose()
      this.radialMask = null
    }
    
    if (this.gridGroup) {
      this.gridGroup.traverse(obj => {
        if (obj.geometry) obj.geometry.dispose()
        if (obj.material) {
          if (Array.isArray(obj.material)) {
            obj.material.forEach(m => m.dispose())
          } else {
            obj.material.dispose()
          }
        }
      })
      this.parent.remove(this.gridGroup)
      this.gridGroup = null
    }
  }
}
